import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { CustomerTable } from "@/components/backoffice/customer/customer-table";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import Paginat from "@/components/lib/paginat";
import useAuth from "@/hooks/useAuth";
import useDebounce from "@/hooks/useDebounce";
import CustomCard from "@/components/backoffice/all-staff/card";
import { FaUser, FaUserCheck, FaUserXmark } from "react-icons/fa6";
import {
  activeCustomerById,
  banCustomerById,
  fetchCustomers,
  toggleCustomerPointBase,
  unbanAddressById,
} from "@/redux/slice/customer-slice/customer-slice";
import AddNewCustomerDialog from "@/components/customer/add-new-customer-dialog";
import hasPermission from "@/hooks/hasPermission";

function Customer() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { customers, stats, pagination, getLoading, statusUpdateLoading } = useSelector(
    (state) => state.customer.customer,
  );

  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("0");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [togglingCustomerId, setTogglingCustomerId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [openAddCustomerDialog, setOpenAddCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const debouncedSearch = useDebounce(searchInput, 500);
  const restaurantId = user?.restaurant_id || 1;

  const visibleCustomers = customers || [];

  const totalCustomers = stats?.total_customers ?? pagination?.total ?? customers?.length ?? 0;
  const bannedCustomers =
    stats?.total_banned_customers ??
    (customers || []).filter((customer) => customer?.is_banned).length;
  const joinedThisMonth = stats?.total_joined_this_month ?? 0;

  useEffect(() => {
    const params = {
      restaurantId,
      page: currentPage,
      per_page: perPage,
      search_term: debouncedSearch,
    };

    if (statusFilter !== "0") {
      params.status = statusFilter;
    }

    dispatch(fetchCustomers(params));
  }, [dispatch, restaurantId, currentPage, perPage, debouncedSearch, statusFilter]);

  const handleTogglePointBase = async (customerId) => {
    if (!customerId) return;

    setTogglingCustomerId(customerId);

    try {
      const response = await dispatch(toggleCustomerPointBase(customerId)).unwrap();
      toast.success(
        response?.message || "Customer point base status updated successfully",
      );
    } catch (error) {
      toast.error(error || "Failed to update customer point base status");
    } finally {
      setTogglingCustomerId(null);
    }
  };

  const handleCustomerStatusChange = async ({
    customerId,
    actionType,
    banAddress,
    unbanCustomer,
    unbanAddress,
    addressId
  }) => {
    if (!customerId) return;

    setStatusUpdatingId(customerId);

    try {
      if (actionType === "ban") {
        const response = await dispatch(banCustomerById({ customerId, payload: { ban_address: banAddress } })).unwrap();
        toast.success(response?.message || "Customer status updated successfully");
      } else {
        const response = await dispatch(activeCustomerById({
          customerId,
          payload: { unban_address: unbanAddress }
        })).unwrap();
        toast.success(response?.message || "Customer status updated successfully");
      }
    } catch (error) {
      toast.error(error || "Failed to update customer status");
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleOpenAddDialog = () => {
    setEditingCustomer(null);
    setOpenAddCustomerDialog(true);
  };

  const handleOpenEditDialog = (customer) => {
    setEditingCustomer(customer);
    setOpenAddCustomerDialog(true);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "All Customer" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Customer", path: "/backoffice/customer" },
        { name: "All Customer", path: "/backoffice/customer" },
      ]}
    >
      <div className="container flex flex-col gap-4 mt-5">
        <div className="flex justify-between items-start md:items-center md:flex-row flex-col gap-5  w-full">
          <div className="flex gap-5 text-white"></div>
          {
            hasPermission("back_office.customer.add_customer") && (
              <Button
                onClick={handleOpenAddDialog}
                className="bg-cyellow hover:bg-cyellow text-black"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add New Customer
              </Button>
            )
          }

        </div>

        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Total Customers Card */}
          <div className="bg-[#1A1D21] border border-gray-800 hover:border-gray-700 rounded-xl p-5 flex items-center justify-between transition-all duration-300 hover:scale-[1.02] shadow-lg group">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-xl bg-amber-500/10 text-amber-500 group-hover:scale-110 transition-transform duration-300">
                <FaUser className="text-2xl" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-400">Total Customer</span>
                <span className="text-3xl font-bold text-white mt-1">
                  {totalCustomers !== undefined && totalCustomers !== null ? String(totalCustomers).padStart(2, '0') : "00"}
                </span>
              </div>
            </div>
            {stats?.total_customers_percentage !== undefined && stats?.total_customers_percentage !== null && (
              <div className="flex items-center gap-1 bg-cgreen/10 text-cgreen border border-cgreen/20 px-2.5 py-1 rounded-full text-xs font-semibold">
                <Plus className="w-3.5 h-3.5" />
                {stats.total_customers_percentage}%
              </div>
            )}
          </div>

          {/* Total Banned Customers Card */}
          <div className="bg-[#1A1D21] border border-gray-800 hover:border-gray-700 rounded-xl p-5 flex items-center justify-between transition-all duration-300 hover:scale-[1.02] shadow-lg group">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-xl bg-rose-500/10 text-rose-500 group-hover:scale-110 transition-transform duration-300">
                <FaUserXmark className="text-2xl" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-400">Total Banned Customer</span>
                <span className="text-3xl font-bold text-white mt-1">
                  {bannedCustomers !== undefined && bannedCustomers !== null ? String(bannedCustomers).padStart(2, '0') : "00"}
                </span>
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 bg-gray-500/5 border border-gray-500/10 px-2.5 py-1 rounded-full">
              Restricted
            </div>
          </div>

          {/* Total Joined This Month Card */}
          <div className="bg-[#1A1D21] border border-gray-800 hover:border-gray-700 rounded-xl p-5 flex items-center justify-between transition-all duration-300 hover:scale-[1.02] shadow-lg group">
            <div className="flex items-center gap-4">
              <div className="p-4 rounded-xl bg-emerald-500/10 text-emerald-500 group-hover:scale-110 transition-transform duration-300">
                <FaUserCheck className="text-2xl" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-400">Total Join This Month</span>
                <span className="text-3xl font-bold text-white mt-1">
                  {joinedThisMonth !== undefined && joinedThisMonth !== null ? String(joinedThisMonth).padStart(2, '0') : "00"}
                </span>
              </div>
            </div>
            {stats?.total_joined_this_month_percentage !== undefined && stats?.total_joined_this_month_percentage !== null && (
              <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                Number(stats.total_joined_this_month_percentage) >= 0 
                  ? "bg-cgreen/10 text-cgreen border-cgreen/20" 
                  : "bg-rose-500/10 text-rose-500 border-rose-500/20"
              }`}>
                {Number(stats.total_joined_this_month_percentage) >= 0 ? "+" : ""}
                {stats.total_joined_this_month_percentage}%
              </div>
            )}
          </div>
        </div>

        <Card className="bg-cblack3 border-0">
          <CardContent className="flex justify-between flex-col gap-5 items-center mt-4">
            <div className="w-full flex justify-between lg:flex-row flex-col gap-5 items-start lg:items-center">
              <div className="flex justify-between items-center gap-3">
                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label className="text-base text-white" htmlFor="customer-search">
                    Customer Name/ID
                  </Label>
                  <Input
                    className="text-gray-300 w-52"
                    type="text"
                    id="customer-search"
                    value={searchInput}
                    onChange={(e) => {
                      setCurrentPage(1);
                      setSearchInput(e.target.value);
                    }}
                    placeholder="Search by Name/ID"
                  />
                </div>

                <div className="grid w-full max-w-sm items-center gap-1.5">
                  <Label className="text-base text-white" htmlFor="">
                    Status
                  </Label>
                  <div className="relative">
                    <Select
                      value={statusFilter}
                      onValueChange={(value) =>
                        setStatusFilter(value)
                      }
                    >
                      <SelectTrigger className="w-[110px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">All</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="banned">Banned</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="flex flex-col  gap-1.5 justify-end items-end">
                <h3 className="text-color7">
                  Total Customers: {totalCustomers || "0"}
                </h3>
                <div className="flex gap-3 justify-center items-center">
                  <Button className="bg-cblack5 border border-gray-600 text-white">
                    Export
                  </Button>
                  <div className="flex gap-1 justify-center items-center">
                    <h3 className="text-white">Show</h3>
                    <Select
                      onValueChange={(value) => setPerPage(Number(value))}
                      value={perPage.toString()}
                    >
                      <SelectTrigger className="w-[60px]">
                        <SelectValue placeholder="10" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="40">40</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full overflow-x-auto">
              {getLoading ? (
                <OnlyTableSekeleton header={false} row={perPage} columns={9} />
              ) : (
                <CustomerTable
                  tableData={visibleCustomers}
                  onTogglePointBase={handleTogglePointBase}
                  togglingCustomerId={togglingCustomerId}
                  onCustomerStatusChange={handleCustomerStatusChange}
                  statusUpdatingId={statusUpdatingId}
                  statusUpdateLoading={statusUpdateLoading}
                  onEditCustomer={handleOpenEditDialog}
                />
              )}

              <Paginat
                className="mt-2"
                pagination={pagination}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <AddNewCustomerDialog
        open={openAddCustomerDialog}
        onOpenChange={(isOpen) => {
          setOpenAddCustomerDialog(isOpen);
          if (!isOpen) {
            setEditingCustomer(null);
          }
        }}
        customerData={editingCustomer}
      />
    </Layout2>
  );
}

export default Customer;
